/**
 * War Room Audit Trail (Sprint 8)
 *
 * Lists persisted fleet-commit audits from Firestore and opens JSON detail.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { ClipboardList, RefreshCw, Loader2, ChevronRight } from 'lucide-react';
import { titanApi, ApiError } from '../lib/api';
import type { WarRoomAuditSummaryRow } from '../types/titan';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';

const fmtUsd = (n: number | null | undefined) =>
  n == null || Number.isNaN(Number(n)) ? '—' : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const fmtPct = (n: number | null | undefined) =>
  n == null || Number.isNaN(Number(n)) ? '—' : `${Number(n).toFixed(2)}%`;

const WarRoomAudit: React.FC = () => {
  const [rows, setRows] = useState<WarRoomAuditSummaryRow[]>([]);
  const [listError, setListError] = useState<string | null>(null);
  const [backendNote, setBackendNote] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailDate, setDetailDate] = useState<string | null>(null);
  const [detailJson, setDetailJson] = useState<string>('');
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [includeRaw, setIncludeRaw] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    setListError(null);
    setBackendNote(null);
    try {
      const res = await titanApi.warRoomAuditList({ limit: 60 });
      setRows(res.audits || []);
      if (res.error && res.error !== 'firestore_unavailable') {
        setBackendNote(res.error);
      }
    } catch (e) {
      const msg = e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Failed to load audits';
      setListError(msg);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const openDetail = (auditDate: string) => {
    setDetailDate(auditDate);
    setDetailOpen(true);
  };

  useEffect(() => {
    if (!detailOpen || !detailDate) return;
    let cancelled = false;
    (async () => {
      setDetailLoading(true);
      setDetailError(null);
      setDetailJson('');
      try {
        const data = await titanApi.warRoomAuditDetail(detailDate, includeRaw);
        if (!cancelled) setDetailJson(JSON.stringify(data, null, 2));
      } catch (e) {
        if (!cancelled) {
          const msg = e instanceof ApiError ? e.detail : e instanceof Error ? e.message : 'Load failed';
          setDetailError(msg);
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [includeRaw, detailOpen, detailDate]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="hidden sm:flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <ClipboardList className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="font-display text-xl sm:text-2xl font-bold text-foreground">War Room audit</h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-xl">
              Daily fleet-commit bundles: regime tags, planned bots, template scorecard rows, learnings,
              futures policy, and weekly mandate resolution. Stored when the controller applies the War Room fleet.
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => loadList()} disabled={loading} className="shrink-0">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {listError && (
        <Alert variant="destructive">
          <AlertDescription>{listError}</AlertDescription>
        </Alert>
      )}

      {backendNote && (
        <Alert>
          <AlertDescription className="text-sm">
            Backend note: {backendNote}
          </AlertDescription>
        </Alert>
      )}

      <div className="rounded-xl border bg-card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground gap-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            Loading audits…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-14 px-4 text-center text-muted-foreground text-sm">
            No audit documents yet. Run the War Room with fleet apply enabled and a connected controller so audits persist to Firestore.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[110px]">Date</TableHead>
                <TableHead>Bias / risk</TableHead>
                <TableHead>Fleet</TableHead>
                <TableHead className="text-right">Equity</TableHead>
                <TableHead className="text-right">Weekly mandate</TableHead>
                <TableHead className="text-right">Bots</TableHead>
                <TableHead className="w-[90px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.date} className="cursor-pointer hover:bg-muted/40" onClick={() => openDetail(r.date)}>
                  <TableCell className="font-mono text-xs">{r.date}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1.5 items-center">
                      <Badge variant="secondary">{r.overall_bias || '—'}</Badge>
                      <span className="text-xs text-muted-foreground">{r.risk_level || ''}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-xs font-mono">{r.fleet_mode || '—'}</span>
                    {r.war_room_activation_mode && (
                      <span className="text-xs text-muted-foreground ml-2">act: {r.war_room_activation_mode}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-sm">{fmtUsd(r.account_equity)}</TableCell>
                  <TableCell className="text-right text-xs">
                    <div>{fmtUsd(r.weekly_profit_target_usd)} / {fmtUsd(r.weekly_max_loss_usd)}</div>
                    <div className="text-muted-foreground">{fmtPct(r.weekly_profit_pct)} / {fmtPct(r.weekly_loss_pct)}</div>
                  </TableCell>
                  <TableCell className="text-right text-sm">
                    {r.bots_created_count ?? '—'} / {r.bots_planned_count ?? '—'}
                    {r.template_scorecard_rows != null && (
                      <div className="text-[11px] text-muted-foreground">scorecard {r.template_scorecard_rows}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <ChevronRight className="w-4 h-4 text-muted-foreground inline" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Audit detail
              {detailDate && <span className="font-mono text-sm font-normal text-muted-foreground">{detailDate}</span>}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-3 pb-2">
            <Switch id="raw-ai" checked={includeRaw} onCheckedChange={setIncludeRaw} />
            <Label htmlFor="raw-ai" className="text-sm cursor-pointer">
              Include raw AI response (large)
            </Label>
          </div>
          {detailError && (
            <Alert variant="destructive">
              <AlertDescription>{detailError}</AlertDescription>
            </Alert>
          )}
          <ScrollArea className="flex-1 min-h-[240px] rounded-md border bg-muted/30">
            {detailLoading ? (
              <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin" />
                Loading…
              </div>
            ) : (
              <pre className="text-[11px] leading-relaxed p-4 whitespace-pre-wrap break-words font-mono">
                {detailJson || '—'}
              </pre>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WarRoomAudit;
